import { useTranslation } from 'react-i18next'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { ScoreDashboard } from './ScoreDashboard'
import { PotentialStrengthsWeaknesses } from './PotentialStrengthsWeaknesses'
import { PotentialQuickWins } from './PotentialQuickWins'
import { PotentialRemakeCta } from './PotentialRemakeCta'
import { KeyMetricsRow } from './KeyMetricsRow'
import { Button } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'
import { ChevronRight } from 'lucide-react'

interface PotentialCheckViewProps {
  report: AuditReport
  onViewFull: () => void
  onStartRemake?: () => void
}

export function PotentialCheckView({ report, onViewFull, onStartRemake }: PotentialCheckViewProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.potentialCheck.${key}`)
  const isNewProduct = report.audit_mode === 'new_product'

  const topStrengths = getTopItems(report.strengths, 'primary', 3)
  const topWeaknesses = getTopWeaknesses(report.weaknesses, 3)
  const topActions = [...(report.product_improvements ?? [])]
    .sort((a, b) => b.priority_score - a.priority_score)
    .slice(0, 3)

  return (
    <section aria-label={r('sectionLabel')} className="potential-check space-y-4">
      {!isNewProduct && report.scores && (
        <ScoreDashboard scores={report.scores} />
      )}

      <PotentialStrengthsWeaknesses strengths={topStrengths} weaknesses={topWeaknesses} />

      {topActions.length > 0 && <PotentialQuickWins actions={topActions} />}

      <KeyMetricsRow
        pricingAnalysis={report.pricing_analysis}
        keywordMatrix={report.keyword_matrix}
        reviewInsights={report.review_insights}
      />

      {onStartRemake && <PotentialRemakeCta onStartRemake={onStartRemake} />}

      <Button
        id={EID['audit-potential-view-full-btn']}
        variant="ghost"
        className="potential-check-view-full-btn w-full"
        onClick={onViewFull}
      >
        {r('viewFullAudit')}
        <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </section>
  )
}

/* ---------- Helpers ---------- */

function getTopItems<T extends { classification: string }>(
  items: T[] | undefined, preferred: string, count: number,
): T[] {
  if (!items?.length) return []
  const preferred_ = items.filter((i) => i.classification === preferred)
  return preferred_.length >= count ? preferred_.slice(0, count) : items.slice(0, count)
}

function getTopWeaknesses(items: AuditReport['weaknesses'] | undefined, count: number) {
  if (!items?.length) return []
  const critical = items.filter((w) => w.classification === 'critical')
  if (critical.length >= count) return critical.slice(0, count)
  return [...items].sort((a, b) => a.priority - b.priority).slice(0, count)
}
